import { useCallback, useEffect, useState } from 'react'
import { Container } from '@/components/layout/Container'
import { AccountSidebar } from '@/components/account/AccountSidebar'
import { SignedInOnly } from '@/components/account/SignedInOnly'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useAuth } from '@/hooks/useAuth'
import {
  listAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  type CustomerAddress,
  type CustomerAddressInput,
} from '@/lib/api'
import { Pencil, Plus, Trash2 } from 'lucide-react'

const emptyForm: CustomerAddressInput = {
  label: 'Home',
  recipientName: '',
  phone: '',
  street: '',
  city: '',
  state: '',
  lga: '',
  zip: '',
}

export function AccountAddressesPage() {
  const { refreshUser } = useAuth()
  const [addresses, setAddresses] = useState<CustomerAddress[] | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<CustomerAddressInput>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setAddresses(await listAddresses())
    } catch {
      setError('Could not load your addresses.')
    }
  }, [])

  useEffect(() => {
    refreshUser()
    load()
  }, [load, refreshUser])

  const startCreate = () => {
    setEditingId(null)
    setForm(emptyForm)
  }

  const startEdit = (a: CustomerAddress) => {
    setEditingId(a.id)
    setForm({
      label: a.label,
      recipientName: a.recipientName,
      phone: a.phone,
      street: a.street,
      city: a.city,
      state: a.state,
      lga: a.lga ?? '',
      zip: a.zip ?? '',
      isDefault: a.isDefault,
    })
  }

  const setField = (key: keyof CustomerAddressInput, value: string | boolean) =>
    setForm((f) => ({ ...f, [key]: value }))

  const handleSave = async () => {
    if (!form.recipientName || !form.street || !form.city || !form.state) {
      setError('Please fill in the recipient, street, city and state.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const result =
        editingId == null ? await createAddress(form) : await updateAddress(editingId, form)
      if (result) {
        setAddresses(result)
        startCreate()
      } else {
        setError('Could not save the address.')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('Remove this address?')) return
    const result = await deleteAddress(id)
    if (result) setAddresses(result)
  }

  return (
    <div className="pt-28 pb-24">
      <Container className="max-w-6xl">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
          <AccountSidebar activePath="/account/addresses" />

          <div>
            <SignedInOnly>
              <p className="mb-2 text-xs uppercase tracking-[0.2em] text-wd-orange">account</p>
            <h1 className="font-display text-4xl normal-case text-carbon md:text-5xl">Address Book</h1>

            {error && <p className="mt-6 text-sm text-carbon">{error}</p>}

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {addresses === null && <p className="text-sm text-carbon/60">Loading your addresses...</p>}

              {addresses?.map((a) => (
                <div key={a.id} className="border border-ink/10 p-5">
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-carbon">
                      {a.label}
                      {a.isDefault && (
                        <span className="ml-2 text-xs font-medium tracking-[0.12em] text-carbon">
                          Default
                        </span>
                      )}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEdit(a)}
                        aria-label={`Edit ${a.label}`}
                        className="text-carbon/50 transition-colors hover:text-wd-orange"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(a.id)}
                        aria-label={`Delete ${a.label}`}
                        className="text-carbon/50 transition-colors hover:text-wd-orange"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-carbon">{a.recipientName}</p>
                  <p className="text-sm text-carbon/60">
                    {a.street}, {a.city}
                    {a.lga ? `, ${a.lga}` : ''}
                  </p>
                  <p className="text-sm text-carbon/60">
                    {a.state} {a.zip ? `, ${a.zip}` : ''}, {a.country}
                  </p>
                  <p className="mt-1 text-sm text-carbon/60">{a.phone}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 border border-ink/10 p-6 sm:p-8">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-carbon">
                  {editingId == null ? 'Add an address' : 'Edit address'}
                </h2>
                <Button variant="ghost" onClick={startCreate} className="px-0 py-0 text-xs">
                  <Plus className="mr-1 h-3 w-3" /> New
                </Button>
              </div>

              <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                <Input
                  label="Label"
                  value={form.label}
                  onChange={(e) => setField('label', e.target.value)}
                  placeholder="Home, Office..."
                />
                <Input
                  label="Recipient name"
                  value={form.recipientName}
                  onChange={(e) => setField('recipientName', e.target.value)}
                />
                <Input
                  label="Phone"
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                />
                <Input
                  label="Street address"
                  value={form.street}
                  onChange={(e) => setField('street', e.target.value)}
                />
                <Input label="City" value={form.city} onChange={(e) => setField('city', e.target.value)} />
                <Input label="State" value={form.state} onChange={(e) => setField('state', e.target.value)} />
                <Input
                  label="LGA (optional)"
                  value={form.lga ?? ''}
                  onChange={(e) => setField('lga', e.target.value)}
                />
                <Input
                  label="Postcode (optional)"
                  value={form.zip ?? ''}
                  onChange={(e) => setField('zip', e.target.value)}
                />
              </div>

              <label className="mt-6 flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.isDefault ?? false}
                  onChange={(e) => setField('isDefault', e.target.checked)}
                  className="h-4 w-4 accent-wd-orange"
                />
                <span className="text-sm text-carbon/70">Set as default delivery address</span>
              </label>

              <div className="mt-6 flex items-center gap-3">
                <Button onClick={handleSave} isLoading={saving}>
                  {editingId == null ? 'Save address' : 'Update address'}
                </Button>
                {editingId != null && (
                  <Button variant="secondary" onClick={startCreate}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
            </SignedInOnly>
          </div>
        </div>
      </Container>
    </div>
  )
}